import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { PlusCircle, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useForm } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";
import { ProfileData } from "@/types/supabase";
import { usePermissions } from '@/services/permissionHelper';
import { listUsersForSubscription } from '@/services/subscriptionAdminService';
import { grantAdminSubscription } from '@/services/subscriptionApi';
import LoadingSpinner from "@/components/LoadingSpinner";

interface SubscriptionFormProps {
  onSubscriptionAdded: () => void;
  prefillUser?: ProfileData | null;
  onPrefillHandled?: () => void;
}

interface FormValues {
  email: string;
  tier: string;
  user_id?: string;
  starts_at: string;
  expires_at: string;
  admin_notes?: string;
}

const today = new Date();
const todayDateValue = today.toISOString().slice(0, 10);
const nextYearDateValue = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())
  .toISOString()
  .slice(0, 10);

const toIsoStartOfDay = (value: string) => new Date(`${value}T00:00:00`).toISOString();
const toIsoEndOfDay = (value: string) => new Date(`${value}T23:59:59`).toISOString();

const defaultFormValues: FormValues = {
  email: '',
  tier: 'premium_tier',
  starts_at: todayDateValue,
  expires_at: nextYearDateValue,
  admin_notes: '',
};

export default function SubscriptionForm({
  onSubscriptionAdded,
  prefillUser = null,
  onPrefillHandled,
}: SubscriptionFormProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [users, setUsers] = useState<ProfileData[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ProfileData | null>(null);
  const { isAdmin } = usePermissions();
  const { toast } = useToast();
  
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormValues>({
    defaultValues: defaultFormValues,
  });
  
  const selectedTier = watch('tier');
  const emailValue = watch("email");

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    
    setIsLoadingUsers(true);
    try {
  const res = await listUsersForSubscription();
  if (!res.success) throw new Error(res.error);
  setUsers(res.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error loading users",
        description: "Could not load user list. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingUsers(false);
    }
  }, [isAdmin, toast]);

  // Load users when popover opens
  useEffect(() => {
    if (popoverOpen && users.length === 0) {
      fetchUsers();
    }
  }, [popoverOpen, users.length, fetchUsers]);

  useEffect(() => {
    if (!prefillUser) {
      return;
    }

    reset({
      ...defaultFormValues,
      email: prefillUser.email,
      user_id: prefillUser.id,
    });
    setSelectedUser(prefillUser);
    setPopoverOpen(false);
    setOpen(true);
    onPrefillHandled?.();
  }, [prefillUser, onPrefillHandled, reset]);

  const handleSelectUser = (user: ProfileData) => {
    setSelectedUser(user);
    setValue('email', user.email);
    setValue('user_id', user.id);
    setPopoverOpen(false);
  };

  const handleAddSubscription = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      // Create or update a subscription record
      if (!data.user_id) {
        throw new Error('Please select a user from the list before granting a subscription.');
      }

      await grantAdminSubscription({
        targetUserId: data.user_id,
        email: data.email,
        tierCode: data.tier as 'whatsapp_tier' | 'premium_tier',
        startsAt: toIsoStartOfDay(data.starts_at),
        expiresAt: toIsoEndOfDay(data.expires_at),
        adminNotes: data.admin_notes || null,
      });

      toast({
        title: "Success",
        description: "Admin subscription saved successfully.",
      });

      reset(defaultFormValues);
      setSelectedUser(null);
      setOpen(false);
      onSubscriptionAdded();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to add subscription';
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark">
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Subscription
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Grant Admin Subscription</DialogTitle>
          <DialogDescription>
            Create or replace an admin-managed paid subscription with explicit start and expiry dates.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleAddSubscription)} className="space-y-4">
          <div className="space-y-2">
            <Label>Select User</Label>
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={popoverOpen}
                  className="w-full justify-between"
                >
                  {selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name} (${selectedUser.email})` : "Select user..."}
                  <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0">
                <Command>
                  <CommandInput placeholder="Search users by name or email..." />
                  <CommandList>
                    <CommandEmpty>No users found.</CommandEmpty>
                    <CommandGroup heading="Users">
                      {isLoadingUsers ? (
                        <div className="flex items-center justify-center p-4">
                          <LoadingSpinner />
                        </div>
                      ) : (
                        users.map((user) => (
                          <CommandItem
                            key={user.id}
                            value={`${user.first_name} ${user.last_name} ${user.email}`}
                            onSelect={() => handleSelectUser(user)}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{user.first_name} {user.last_name}</span>
                              <span className="text-xs text-muted-foreground">{user.email}</span>
                            </div>
                          </CommandItem>
                        ))
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              placeholder="user@example.com"
              value={selectedUser?.email || emailValue || ""}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              disabled={!!selectedUser}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
            {selectedUser && (
              <input 
                type="hidden" 
                {...register("user_id")} 
                value={selectedUser.id}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>Subscription Tier</Label>
            <RadioGroup defaultValue={selectedTier} className="space-y-2">
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  value="premium_tier"
                  id="premium_tier"
                  {...register("tier")}
                />
                <Label htmlFor="premium_tier">Premium</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  value="whatsapp_tier"
                  id="whatsapp_tier"
                  {...register("tier")}
                />
                <Label htmlFor="whatsapp_tier">WhatsApp Verification</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="starts_at">Starts</Label>
              <Input id="starts_at" type="date" {...register('starts_at', { required: true })} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expires_at">Expires</Label>
              <Input id="expires_at" type="date" {...register('expires_at', { required: true })} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin_notes">Admin notes</Label>
            <Textarea
              id="admin_notes"
              placeholder="Optional internal notes about this grant"
              {...register('admin_notes')}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Adding..." : "Add Subscription"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
